import React, { useCallback, useEffect, useRef, useState } from 'react';
import { trackEvent } from 'utils/analytics';
import {
    DriverReviewReason,
    DriverReviewRegionalRanking,
    DriverReviewSample,
    DriverReviewsSummary,
    InsightsPeriod,
} from 'types/PropertyInsights';
import { SpotInterface } from 'types';
import { PagedResponse } from 'types/Pagination';
import { HTTPError } from 'clients/HTTPClient';
import { propertyInsightsClient } from 'clients';
import { UrlUpdaterFunctions } from 'hooks/useFilters';
import reviewsImg from 'assets/reviews.png';
import WidgetBox from 'components/widgets/WidgetBox';
import AGSimpleSelect from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import EmptyWidget from 'components/EmptyWidget/EmptyWidget';
import WidgetErrorMessage from 'components/WidgetErrorMessage/WidgetErrorMessage';
import Modal, { ModalRef } from 'components/Modal/Modal';
import {
    MIN_SAMPLES_TO_SHOW_MODAL,
    insightsPeriodOptions,
    ReviewsRating,
    hasRegionalRankingPercentile,
} from '../helpers';
import Summary from './Summary';
import Samples from './Samples';
import Reasons from './Reasons';
import { DriverReviewsSkeleton } from './DriverReviewsSkeleton';
import { ReviewModal } from './ReviewsModal';
import styles from './DriverReviewsWidget.module.css';
import HighlightInfo from '../HighlightInfo/HighlightInfo';

type DriverReviewsWidgetProps = {
    spot: SpotInterface;
    filters: { reviewsPeriod: InsightsPeriod; rating: ReviewsRating };
    urlUpdater: UrlUpdaterFunctions<{ reviewsPeriod: InsightsPeriod; rating: ReviewsRating }>;
};

function DriverReviewsWidget({ spot, filters, urlUpdater }: DriverReviewsWidgetProps): JSX.Element {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [summary, setSummary] = useState<DriverReviewsSummary | null>(null);
    const [samples, setSamples] = useState<PagedResponse<DriverReviewSample> | null>(null);
    const [reasons, setReasons] = useState<DriverReviewReason[] | null>(null);
    const [regionalRanking, setRegionalRanking] = useState<DriverReviewRegionalRanking | null>(null);

    const modalRef = useRef<ModalRef>(null);

    function handleReviewsPeriodSelection(selection: string) {
        urlUpdater.reviewsPeriod(selection);
        trackEvent('Changed Driver Reviews Timeframe', {
            spot: spot.pk,
            spotName: spot.name,
            timeframe: selection,
        });
    }

    function handleRatingSelection(selection: string) {
        urlUpdater.rating(selection);
        trackEvent('Changed Driver Reviews Rating', {
            spot: spot.pk,
            spotName: spot.name,
            rating: selection || 'All',
        });
    }

    function openModal() {
        modalRef.current?.openModal();
    }

    const getDriverReviews = useCallback(async () => {
        setLoading(true);
        setErrorMessage('');
        try {
            const [summary, samples, reasons, regionalRanking] = await Promise.all([
                propertyInsightsClient.getDriverReviewsSummary(spot.uuid, filters.reviewsPeriod),
                propertyInsightsClient.getDriverReviewSamples(spot.uuid, filters.reviewsPeriod, ReviewsRating.ALL, 1),
                propertyInsightsClient.getDriverReviewReasons(spot.uuid, filters.reviewsPeriod),
                propertyInsightsClient.getDriverReviewRegionalRanking(spot.uuid, filters.reviewsPeriod),
            ]);
            setSummary(summary);
            setSamples(samples);
            setReasons(reasons);
            setRegionalRanking(regionalRanking);
        } catch (e) {
            if (HTTPError.isHTTPError(e)) {
                setErrorMessage(e.message);
            } else {
                setErrorMessage(e?.toString() || 'Something went wrong');
            }
        } finally {
            setLoading(false);
        }
    }, [spot.uuid, filters.reviewsPeriod]);

    useEffect(() => {
        getDriverReviews();
    }, [spot.uuid, getDriverReviews, filters.reviewsPeriod]);

    const isLoading = loading || !summary || !samples || !reasons;
    const isEmpty = summary && summary.total_reviews < 5;

    return (
        <WidgetBox
            title="Driver Reviews"
            rightElement={
                <AGSimpleSelect
                    options={insightsPeriodOptions}
                    selected={filters.reviewsPeriod}
                    onSelect={handleReviewsPeriodSelection}
                    width="160px"
                />
            }
        >
            {isEmpty && !isLoading ? (
                <EmptyWidget imgUrl={reviewsImg} />
            ) : errorMessage ? (
                <WidgetErrorMessage message={errorMessage} onRetry={getDriverReviews} />
            ) : (
                <div>
                    {isLoading ? (
                        <DriverReviewsSkeleton />
                    ) : (
                        <div className={styles.driverReviewsWidget}>
                            <Summary summary={summary} />
                            {hasRegionalRankingPercentile(regionalRanking) ? (
                                <HighlightInfo
                                    text={`Your parking lot is among the top ${Math.max(
                                        1,
                                        100 - regionalRanking.regional_percentile
                                    )}%
                                    highest-rated in your region.`}
                                />
                            ) : null}
                            <Samples samples={samples.results} openModal={openModal} />
                            <Reasons reasons={reasons} />
                        </div>
                    )}
                    {samples && samples.results.length > MIN_SAMPLES_TO_SHOW_MODAL && (
                        <Modal title="Driver Reviews" ref={modalRef}>
                            <ReviewModal
                                samples={samples}
                                spotId={spot.uuid}
                                filters={filters}
                                handleReviewsPeriodSelection={handleReviewsPeriodSelection}
                                handleRatingSelection={handleRatingSelection}
                                isLoading={isLoading}
                                reasons={reasons}
                                summary={summary}
                            />
                        </Modal>
                    )}
                </div>
            )}
        </WidgetBox>
    );
}

export default DriverReviewsWidget;
